import React, {useState, useRef, useEffect} from 'react';
import CurrencyMap from "../../utils/CurrencyMap";
import Events from "../../utils/Events";
import Constants from "../../utils/Constants";
import NobiEventTracker from "../../utils/NobiEventTracker";
import DeepMerge from "../../utils/DeepMerge";
import {EntryPoint} from "../Chatbot/EntryPoint";

import styles from "./ProductVariantDisplayer.module.css";
import QuickAddOverlay from "../QuickAddOverlay/QuickAddOverlay";

const ProductVariantDisplayer = ({ productVariant, brandTheme, conversationId, merchantId, entryPoint, assistantConfiguration }) => {
    const ImageUrlForVariantsWithNoImages = "https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png";

    const [overlayActive, setOverlayActive] = useState(false);
    const overlayTimeoutRef = useRef(null);

    useEffect(() => {
        if (!productVariant.product?.variantManifest || productVariant.product?.variantManifest.length === 0) {
            // Clone this and add it to the list of variants
            let clonedVariant = JSON.parse(JSON.stringify(productVariant));
            if (!clonedVariant.size || clonedVariant.size === "") {
                clonedVariant.size = "Add To Cart";
            }
            clonedVariant.internalId = productVariant.internalId;

            if (!productVariant.product) {
                productVariant.product = {};
            }
            productVariant.product.variantManifest = [clonedVariant];
        }
        else if (productVariant.product?.variantManifest.length === 1 && !productVariant.product?.variantManifest[0].size) {
            productVariant.product.variantManifest[0].size = "Add To Cart";
        }
    }, [productVariant]);

    function getImageUrl() {
        let productVariantImages = productVariant.images?.sort((a, b) => a.position - b.position);
        let productVariantImageUrl = productVariantImages?.length > 0
            ? productVariantImages[0].url
            : productVariant.product.images?.length > 0
                ? productVariant.product.images[0].url
                : ImageUrlForVariantsWithNoImages;
        return productVariantImageUrl;
    }

    function getCurrencySymbol() {
        let currencySymbol = "$";
        if (productVariant.currency) {
            currencySymbol = CurrencyMap[productVariant.currency];
            if (!currencySymbol) {
                currencySymbol = productVariant.currency;
            }
        }
        return currencySymbol;
    }

    function trackProductClick(event) {
        const destinationUrl = event.currentTarget.href;

        // Check if the user is using a modifier key or middle-click
        const isModifiedClick = event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1;

        let details = {
            conversationId: conversationId,
            productVariantId: productVariant.id,
            productVariantInternalId: productVariant.internalId
        };

        // Fire off tracking events.
        NobiEventTracker.trackEvent(
            merchantId,
            entryPoint,
            Events.ClickOnSearchResult,
            false,
            true,
            details);
        storeClickEvent();

        // If it's a modified click, let the browser handle it naturally.
        if (isModifiedClick) {
            return;
        }

        // For a normal click, prevent default navigation and redirect after a short delay.
        event.preventDefault();
        setTimeout(() => {
            window.location.href = destinationUrl;
        }, 100);
    }

    function storeClickEvent() {
        const data = {
            conversationId: conversationId,
            timestamp: new Date().toISOString(),
        };

        try {
            // Retrieve existing nobiData from localStorage
            let nobiData = localStorage.getItem(Constants.MasterLocalStorageKey);
            nobiData = nobiData ? JSON.parse(nobiData) : {};

            // Retrieve existing clickedProducts or initialize as empty object
            let storedProductClicks = {};
            if (nobiData[Constants.KeyForClickedProductsInLocalStorage]) {
                storedProductClicks = JSON.parse(nobiData[Constants.KeyForClickedProductsInLocalStorage]);
            }

            // Ensure the product entry exists
            if (!storedProductClicks[productVariant.product.id]) {
                storedProductClicks[productVariant.product.id] = {};
            }

            // Update or add the clicked variant with the latest timestamp
            storedProductClicks[productVariant.product.id][productVariant.internalId] = data;

            // Save the updated clicked variants back to nobiData
            nobiData[Constants.KeyForClickedProductsInLocalStorage] = JSON.stringify(storedProductClicks);

            // Save the entire nobiData back to localStorage
            localStorage.setItem(Constants.MasterLocalStorageKey, JSON.stringify(nobiData));
        }
        catch (error) {
            console.error(`Failed to store clicked variants in localStorage: ${error.message}`);
        }
    }

    function renderProductInfo(displayName, currencySymbol) {
        let hasQuickAddOptions = productVariant?.product?.variantManifest?.length >= 1;

        return <div className={`${styles.infoContent} ${hasQuickAddOptions ? styles.infoContentWithQuickAdd : ""}`}>
            <h3 className={styles.searchResultProductName} style={brandTheme?.products?.title}>
                {displayName}
            </h3>
            {!assistantConfiguration?.hideOutOfStockItems &&
                productVariant.product.areAllVariantsOutOfStock !== null &&
                productVariant.product.areAllVariantsOutOfStock !== undefined &&
                productVariant.product.areAllVariantsOutOfStock && (
                    <p className={styles.searchResultProductOutOfStock}>
                        Out of stock online but may be available in stores, click item above for more details
                    </p>
                )}

            {productVariant.price && (
                <p className={styles.searchResultProductPrice}>
                    {currencySymbol}{productVariant.price.toLocaleString()}
                    {productVariant.compareAtPrice && productVariant.price < productVariant.compareAtPrice && (
                        <span className={styles.searchResultProductCompareAtPrice}>
                            {currencySymbol}{productVariant.compareAtPrice}
                        </span>
                    )}
                </p>
            )}
        </div>;
    }

    function fadeInQuickAddOverlay() {
        // If this product is the only one in the list of all variant sizes, and it's not available, don't show the overlay
        if (productVariant.product?.variantManifest?.length === 0
            || (productVariant.product?.variantManifest?.length === 1 && productVariant.product?.variantManifest && productVariant.product?.variantManifest[0].isAvailable === false)) {
            return;
        }

        return () => {
            overlayTimeoutRef.current = setTimeout(() => {
                setOverlayActive(true);
            }, 200);
        };
    }

    function fadeOutQuickAddOverlay() {
        // If this product is the only one in the list of all variant sizes, and it's not available, don't show the overlay
        if (productVariant.product?.variantManifest.length === 1 && productVariant.product?.variantManifest[0].isAvailable === false) {
            return;
        }

        return () => {
            clearTimeout(overlayTimeoutRef.current);
            setOverlayActive(false);
        };
    }

    function renderProductVariantContent() {
        let productVariantImageUrl = getImageUrl();
        let currencySymbol = getCurrencySymbol();
        let url = productVariant.product?.url;

        if (url && url.includes("?")) {
            url += "&";
        }
        else {
            url += "?";
        }
        url += "variant=" + productVariant.internalId + `&${Constants.UrlParameterForNobiConversationId}=` + conversationId + `&${Constants.UrlParameterForNobiProductVariantId}=` + productVariant.id;

        let displayName = productVariant.product.name;
        let linkStyle = {};
        if (entryPoint && entryPoint === EntryPoint.Cart) {
            linkStyle.height = "100%";
            linkStyle.display = "flex";
            linkStyle.flexDirection = "column";
        };

        let variantsOfTheSameColorOrNoColor = productVariant.product?.variantManifest?.filter(variant => variant.color === productVariant.color || !variant.color);

        return <a href={url} className={styles.productLink} style={linkStyle} aria-expanded={overlayActive} aria-haspopup={productVariant.product?.variantManifest?.length > 1} aria-label={displayName}
                  onClick={trackProductClick}
                  onMouseEnter={fadeInQuickAddOverlay}
                  onMouseLeave={fadeOutQuickAddOverlay}
                  onFocus={fadeInQuickAddOverlay}
                  onBlur={fadeOutQuickAddOverlay}>
            <div className={styles.imageContainer}>
                <img src={productVariantImageUrl} alt={displayName} style={brandTheme?.products?.image}/>
            </div>
            <div className={`nobiProductVariantInfo ${styles.productInfo}`} style={brandTheme?.products?.details}>
                <QuickAddOverlay
                    variants={variantsOfTheSameColorOrNoColor}
                    conversationId={conversationId}
                    merchantId={merchantId}
                    aria-hidden={!overlayActive}
                    entryPoint={entryPoint}
                />
                {renderProductInfo(displayName, currencySymbol)}
            </div>
        </a>;
    }

    // Make the min width of these things 65% when they come from the cart
    let cartStyle = entryPoint === EntryPoint.Cart
        ? {minWidth: "50%", maxWidth: "75% !important", width: "50% !important"}
        : {};
    let appliedStyle = DeepMerge(brandTheme?.products?.container, cartStyle);

    if (!productVariant || !productVariant.product) {
        console.error("Product variant is null");
        return null;
    }

    return <div className={`nobiProductInfoVariant ${styles.productVariant}`} key={productVariant.id} aria-label={"Search result product"} style={appliedStyle}>
        {renderProductVariantContent()}
    </div>
}

export default ProductVariantDisplayer;